import qs from 'query-string'
import get from 'lodash/get'
import { normalizeSubdomainKey, sendRequest } from '@/configs/requests'
import { resolveCustomAssetUrl } from '@/utils/customAssets'
import { resolveOnPrimaryColor, resolvePrimaryColor } from '@/utils/themeColors'

// default themes if nothing returned
const defaultTheme = 
{
  primary: '#0096c7',
  secondary: '#2a4f66'
}

const _ = { get }

export default {
  namespaced: true,
  state: {
    meta: null,
    path: null,
    pages: [],
    faqs: [],
    products: [],
    isLoading: {
      main: false,
      faqs: false,
      products: false
    }
  },
  getters: {
    template(state) {
      return state.meta?.attributes?.template ?? 'default'
    },
    theme(state) {
      const primary = resolvePrimaryColor(state.meta?.attributes?.PrimaryColor)

      return {
        primary,
        onPrimary: resolveOnPrimaryColor(),
        secondary: '#424242'
      }
    },
    subdomain(state) {
      return state.meta?.subdomain
    },
    currentPage(state) {
      const pages = state.pages || []
      const currentPage = pages.find((page) => {
        if (state.path === '/') {
          return page.Id === 1
        }

        return page.URLPath.toLowerCase().slice(0, -1) === state.path
      })

      return currentPage
    },
    menu(state) {
      const allPages = state.pages || []
      const menuPages = allPages
      .filter((page) => (page.IsActive || page.code === 'Home') && page.Id === page.ParentId)
      .map((page) => ({
        Id: page.Id,
        title: page.Title,
        link: page.URLPath === 'Home' ? '/' : page.URLPath.toLowerCase().slice(0, -1)
      }))

      menuPages.forEach((page) => {
        page.childPages = allPages
        .filter((otherPage) => otherPage.IsActive && page.Id === otherPage.ParentId && page.Id !== otherPage.Id)
        .map((childPage) => ({
          Id: childPage.Id,
          title: childPage.Title,
          link: childPage.URLPath === 'Home' ? '/' : childPage.URLPath.toLowerCase().slice(0, -1)
        }))
      })

      return menuPages
    },
    currentBanner(state, getters) {
      const currentPageImage = _.get(getters.currentPage, 'PageData[0].BannerImage', null)

      return {
        banner: currentPageImage
          ? resolveCustomAssetUrl(currentPageImage, state.meta?.custom_url)
          : null,
        title: _.get(getters.currentPage, 'BannerTitle', null)
      }
    },
    routerPaths(state) {
      // maps route name to path
      return (state.pages || []).reduce((acc, page) => {
        let path = page.URLPath.toLowerCase()

        if (page.Code === 'Home') {
          path = ''
        }

        return { ...acc, [page.Code]: path }
      }, {})
    }
  },
  mutations: {
    setMeta(state, meta) {
      state.meta = meta
    },
    setPath(state, path) {
      state.path = path
    },
    setPages(state, pages) {
      state.pages = pages || []
    },
    setProducts(state, products) {
      state.products = products
    },
    setLoading(state, isLoading) {
      state.isLoading = {
        ...state.isLoading,
        ...isLoading
      }
    },
    setFaqs(state, faqs) {
      state.faqs = faqs
    },
    setLoadingFaqs(state, isLoadingFaqs) {
      state.isLoadingFaqs = isLoadingFaqs 
    },
    setLoadingProducts(state, isLoadingProducts) {
      state.isLoadingProducts = isLoadingProducts 
    }
  },
  actions: {
    async getMeta( { commit } ) {

      commit('setLoading', { main: true })

      // eslint-disable-next-line prefer-const
      let [response, pageResponse] = await Promise.all([
        sendRequest('microsite', 'GET'),
        sendRequest('microsite/pages', 'GET')])

      if (!response) {
        response = {
          attributes: defaultTheme
        }
      }
      // TODO add template to attributes
      response.attributes.template = 'default'

      const subdomain = normalizeSubdomainKey(response.custom_url)

      commit('setMeta', { ...response, subdomain })
      commit('setPages', pageResponse || [])
      commit('setLoading', { main: false })
    },
    async getFaqs( { commit } ) {

      commit('setLoading', { faqs: true })
      const response  = await sendRequest('microsite/faqs', 'GET')

      commit('setFaqs', response)
      commit('setLoading', { faqs: false })
    },
    async getProducts( { commit } ) {

      commit('setLoading', { products: true })
      const response  = await sendRequest('microsite/programs', 'GET')

      commit('setProducts', response)
      commit('setLoading', { products: false })
    }
  }
}
